import React from 'react';
import styled from 'styled-components';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { ScrollingCarousel } from '@trendyol-js/react-carousel';
import { useState } from 'react';

import { AnimateIn, Container, colors, media, Button, Text, fontSizes, animation } from '../ui-components';

import SvgQuote from '../assets/icons/quote.jsx';
/**
 * @component QuoteCarousel
 * 
 * @param {object} props
 * 
 * @prop {array} quotes
 * 
 * @return {JSX.Element}
 */
const QuoteCarousel = props => {
  const { quotes } = props;

  const renderSingleQuote = (quote, index) => {
    const [isActive, setIsActive] = useState(false);
    const image = getImage(quote?.avatarData?.fluid);

    const showQuote = () => {
      if (!isActive) {
        setIsActive(true);
      } else {
        setIsActive(false);
      }
    };

    const authorFirstName = () => {
      return quote.author.substring(0, quote.author.indexOf(' '));
    };

    return (
      <ImageDiv id={index} isActive={isActive}>
        {image && <QuoteImage id={index} image={image} alt={quote.author} isActive={isActive}/>}
        <QuoteImageShadowOverlay isActive={isActive} />
        <QuoteButton id={index} isActive={isActive} onClick={showQuote}>
          <ButtonText
            id={index}
            isActive={isActive}
            onClick={showQuote}
          >{`Click To See ${authorFirstName()}'s Message`}</ButtonText>
          <QuoteContents id={index} isActive={isActive}>
            <QuoteInput>
              <QuoteIconImage isActive={isActive}>
                <SvgQuote alt={'quotation mark image'} />
              </QuoteIconImage>
              <QuoteTextWrapper>
                <QuoteText className={'overflow-scroll-gradient-scroller'}>{quote?.quote}</QuoteText>
                <GradientDiv className={'overflow-scroll-gradient'} isActive={isActive}></GradientDiv>
                <HoverGradientDiv className={'overflow-scroll-hover-gradient'} isActive={isActive}></HoverGradientDiv>
              </QuoteTextWrapper>
              <AuthorWrapper>
                <AuthorName size={'large'}>{quote.author}</AuthorName>
                <AuthorTitle>{quote.authorSubtitle}</AuthorTitle>
              </AuthorWrapper>
            </QuoteInput>
          </QuoteContents>
        </QuoteButton>
      </ImageDiv>
    );
  };

  const renderQuotes = () => {
    return quotes.map((quote, index) => <>{renderSingleQuote(quote, index)}</>);
  };

  return (
    <SliderContainer>
      <AnimateIn onScroll>
        <DesktopSlider>
          <QuoteWrapper>{quotes && renderQuotes()}</QuoteWrapper>
        </DesktopSlider>
        <MobileSlider>{quotes && renderQuotes()}</MobileSlider>
      </AnimateIn>
    </SliderContainer>
  );
};

const QuoteWrapper = styled.div`
  display: flex;
  gap: 40px;

  @media screen and (min-width: ${media.hugeBottom}) {
    padding-left: calc(((100vw - ${media.hugeBottom}) / 2) + 80px);
    padding-right: calc(((100vw - ${media.hugeBottom}) / 2) + 80px);
  }
  @media screen and (width <= ${media.hugeBottom}) {
    padding-left: 80px;
    padding-right: 80px;
  }
  @media screen and (width <= ${media.mediumTop}) {
    padding-left: 20px;
    padding-right: 20px;
  }
`;

const ImageDiv = styled.div`
  height: 668px;
  width: 550px;
  position: relative;
  @media screen and (max-width: ${media.mediumBottom}) {
    height: calc(1.2145 * (100vw - 40px));
    width: 100%;
  }
`;

const QuoteImage = styled(GatsbyImage)`
  height: ${({ isActive }) => (isActive ? '586px' : '668px')};
  width: ${({ isActive }) => (isActive ? '482.485px;' : '550px')};
  position: absolute;
  top: 0;
  left: 0;
  transition: ${animation.general.transition};

  ::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;

  @media screen and (max-width: ${media.mediumBottom}) {
    height: ${({ isActive }) =>
      isActive ? 'calc(0.8772 * (1.2145 * (100vw - 40px)))' : 'calc(1.2145 * (100vw - 40px))'};
    width: ${({ isActive }) => (isActive ? 'calc(0.8772 * (100vw - 40px))' : '100%')};
  }
`;

const QuoteImageShadowOverlay = styled.div`
  height: ${({ isActive }) => (isActive ? '586px' : '668px')};
  width: ${({ isActive }) => (isActive ? '482.485px;' : '550px')};
  position: absolute;
  top: 0;
  left: 0;
  transition: ${animation.general.transition};
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.4) 100%);
  opacity: ${({ isActive }) => (isActive ? '1' : '0')};

  @media screen and (max-width: ${media.mediumBottom}) {
    height: ${({ isActive }) =>
      isActive ? 'calc(0.8772 * (1.2145 * (100vw - 40px)))' : 'calc(1.2145 * (100vw - 40px))'};
    width: ${({ isActive }) => (isActive ? 'calc(0.8772 * (100vw - 40px))' : '100%')};
  }
`;

const QuoteButton = styled(Button)`
  display: flex;
  position: absolute;
  bottom: 0;
  right: 0;
  height: ${({ isActive }) => (isActive ? '586px' : '0px')};
  width: ${({ isActive }) => (isActive ? '482.485px' : '0px')};
  min-height: 48px;
  min-width: fit-content;
  transition: ${animation.general.transition};

  @media screen and (max-width: ${media.mediumBottom}) {
    height: ${({ isActive }) => (isActive ? 'calc(0.8772 * (1.2145 * (100vw - 40px)))' : '0')};
    width: ${({ isActive }) => (isActive ? 'calc(0.8772 * (100vw - 40px))' : '0')};
  }
`;

const ButtonText = styled(Text)`
  color: ${colors.pureWhite};
  font-size: 14px;
  font-weight: 900;
  line-height: normal;
  opacity: ${({ isActive }) => (isActive ? '0' : '1')};
  transition: opacity ${animation.general.durationAndTiming};
`;

const QuoteContents = styled.div`
  display: flex;
  justify-content: center;
  position: absolute;
  height: ${({ isActive }) => (isActive ? '586px' : '0')};
  width: ${({ isActive }) => (isActive ? '482.485px' : '0')};
  background-color: ${colors.buttonDefault};
  bottom: 0;
  right: 0;
  padding: 40px;
  opacity: ${({ isActive }) => (isActive ? '1' : '0')};
  transition: ${animation.general.transition};
  z-index: ${({ isActive }) => (isActive ? '2' : '-99')};

  p {
    color: ${colors.pureWhite};
  }

  &:hover {
    background-color: ${colors.buttonHover};

    .overflow-scroll-hover-gradient {
      opacity: 1;
      transition: opacity ${animation.general.durationAndTiming};
    }
    .overflow-scroll-gradient {
      opacity: 0;
      transition: opacity ${animation.general.durationAndTiming};
    }
  }

  .overflow-scroll-hover-gradient {
    opacity: 0;
    transition: opacity ${animation.general.durationAndTiming};
  }
  .overflow-scroll-gradient {
    opacity: 1;
    transition: opacity ${animation.general.durationAndTiming};
  }

  @media screen and (max-width: ${media.mediumBottom}) {
    height: ${({ isActive }) => (isActive ? 'calc(0.8772 * (1.2145 * (100vw - 40px)))' : '0')};
    width: ${({ isActive }) => (isActive ? 'calc(0.8772 * (100vw - 40px))' : '0')};
    padding: 28px;
  }
`;

const QuoteIconImage = styled.div`
  position: absolute;
  top: -63.965px;
  left: -129.625px;
  opacity: ${({ isActive }) => (isActive ? '1' : '0')};
  transition: opacity ${animation.general.durationAndTiming};

  @media screen and (max-width: ${media.mediumBottom}) {
    left: -75.83px;
    top: -63.965px;

    svg {
      height: 74px;
      width: 100.601px;
    }
  }
`;

const QuoteInput = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  justify-content: space-between;
  position: relative;
  padding-top: 60px;

  @media screen and (max-width: ${media.mediumBottom}) {
    padding-top: 20px;
  }
`;

const GradientDiv = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 31px;
  left: 0;
  background: linear-gradient(180deg, rgba(83, 121, 153, 0) 0%, ${colors.linkDefault} 100%);
  opacity: 1;
  transition: ${animation.general.transition};
  @media screen and (max-width: ${media.mediumBottom}) {
    bottom: 0;
  }
`;

const HoverGradientDiv = styled.div`
  position: absolute;
  width: 100%;
  bottom: 0;

  content: '';
  display: block;
  background: linear-gradient(180deg, rgba(63, 91, 116, 0) 0%, ${colors.linkHover} 100%);
  height: 32px;
  opacity: 0;
  z-index: 5;

  @media screen and (max-width: ${media.mediumBottom}) {
    bottom: 0;
  }
`;

const QuoteTextWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

const QuoteText = styled(Text)`
  &.overflow-scroll-gradient-scroller {
    font-size: ${fontSizes.quote.fontSize};
    line-height: ${fontSizes.quote.lineHeight};
    text-transform: none;
    text-align: left;
    overflow-y: scroll !important;
    width: 100%;
    height: 316px;
    padding-bottom: 40px;

    ::-webkit-scrollbar {
      display: none;
    }
    scrollbar-width: none;

    @media screen and (max-width: ${media.mediumBottom}) {
      font-size: ${fontSizes.paragraph.medium.mobile.fontSize};
      line-height: ${fontSizes.paragraph.medium.mobile.lineHeight};

      height: calc(0.8772 * (1.2145 * (100vw - 40px)) - 186px);
    }
  }
`;

const AuthorWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 4px;
  text-transform: none;
  text-align: end;
`;

const AuthorName = styled(Text)``;

const AuthorTitle = styled(Text)``;

const SliderContainer = styled(props => <Container {...props} />)`
  max-width: none;
  padding: 0 0 120px 0;
  position: relative;

  @media screen and (max-width: ${media.mediumBottom}) {
    padding: 0 20px 60px 20px;
  }
`;

const DesktopSlider = styled(props => <ScrollingCarousel {...props} />)`
  div {
    overflow-y: hidden;
  }

  div div {
    overflow-y: unset;
  }

  @media screen and (max-width: ${media.mediumBottom}) {
    display: none;
  }
`;

const MobileSlider = styled(props => <div {...props} />)`
  display: flex;
  align-items: center;
  flex-direction: column;
  position: relative;
  gap: 28px;
  overflow: hidden;

  @media screen and (min-width: 769px) {
    display: none;
  }
`;

export default QuoteCarousel;
